import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  StatusMessagePosition,
  StatusMessageType,
} from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";

import { ReactQueryKeysAccount } from "../queryKeys";
import { WorkspaceEngine, WorkspaceEngineStatus } from "./engine.types";
import { abortStartForEngine } from "./startAbortRegistry";
import { stopEngineMutation } from "./stopEngine";

export const useStopWorkspaceEngine = (mutation = stopEngineMutation) => {
  const queryClient = useQueryClient();
  const { putStatusMessage } = useStatusMessageQueue();
  const { mutate, isPending } = useMutation({
    mutationFn: mutation,
    onMutate: async (engineName: string) => {
      // Abort pending start for this engine if any
      abortStartForEngine(engineName);

      // Cancel any in-flight queries
      await queryClient.cancelQueries({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
        exact: false,
      });

      // Update all matching queries with optimistic state
      const queries = queryClient.getQueriesData<WorkspaceEngine[]>({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
        exact: false,
      });

      queries.forEach(([queryKey]) => {
        queryClient.setQueryData<WorkspaceEngine[]>(queryKey, engines => {
          return (engines || []).map(engine => {
            if (engine.engineName === engineName) {
              return {
                ...engine,
                status: WorkspaceEngineStatus.STOPPING,
              };
            }
            return engine;
          });
        });
      });
    },
    onError: async (error: Error) => {
      putStatusMessage({
        type: StatusMessageType.Error,
        message: error.message,
        options: {
          id: "stop-engine-error",
          insertToPosition: StatusMessagePosition.Top,
          autoRemove: true,
        },
      });

      // On error, invalidate to refetch fresh data
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
        exact: false,
      });
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
